import { ref } from 'vue';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { ACTIONS } from '@/types/taskActions';
import { IDueDates } from '@/types/dueDates';

/**
 * Composable for managing due dates
 */
export function useDueDates() {
  const dueDates = ref<IDueDates[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);

  /**
   * Adjusts a date to skip weekends if consider_workdays is true
   * @param date The date to adjust
   * @param considerWorkdays Whether to consider workdays
   * @returns The adjusted date
   */
  function adjustForWorkdays(date: Date, considerWorkdays: boolean): Date {
    if (!considerWorkdays) {
      return date;
    }
    
    const dayOfWeek = date.getDay();
    
    // If it's Saturday (6) or Sunday (0), add 48 hours to skip to Monday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      const adjustedDate = new Date(date.getTime() + (48 * 60 * 60 * 1000));

      logger.info('Adjusting due date for weekend', LogCategory.GENERAL, {
        originalDate: date.toISOString(),
        dayOfWeek,
        adjustedDate: adjustedDate.toISOString()
      });
      
      return adjustedDate;
    }
    
    return date;
  }

  /**
   * Creates a default due date (current date + 48 hours)
   * @returns ISO string of the default due date
   */
  function createDefaultDueDate(): string {
    const currentDate = new Date();
    const defaultDueDate = new Date(currentDate.getTime() + (48 * 60 * 60 * 1000));
    
    return defaultDueDate.toISOString();
  }

  /**
   * Get due date for a specific action and document
   * @param action The action type
   * @param documentId The document ID
   * @returns Promise resolving to the due date or null if not found
   */
  async function getDueDate(action: ACTIONS, documentId: string): Promise<string> {
    loading.value = true;
    error.value = null;

    try {
      // Validate parameters
      if (!action) {
        logger.error('Invalid action provided', LogCategory.GENERAL, { action });
        error.value = new Error('Action is required');
        return 'Invalid action';
      }

      if (!documentId) {
        logger.error('Invalid documentId provided', LogCategory.GENERAL, { documentId });
        error.value = new Error('Document ID is required');
        return 'Invalid document ID';
      }

      // Fetch the document to get its report type
      const { data: document, error: documentError } = await supabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .single();

      if (documentError) {
        logger.error('Error fetching document', LogCategory.DATABASE, {
          error: documentError,
          documentId
        });
        throw documentError;
      }

      if (!document) {
        logger.error('Document not found', LogCategory.DATABASE, { documentId });
        error.value = new Error(`Document with ID ${documentId} not found`);
        return createDefaultDueDate();
      }
      const reportType = document.report_type;

      // Fetch all due date records where action equals the action parameter
      const { data: dueDates, error: dueDateError } = await supabase
        .from('due_dates')
        .select('*')
        .eq('action', action);

      if (dueDateError) {
        logger.error('Error fetching due dates', LogCategory.DATABASE, {
          error: dueDateError,
          action
        });
        logger.warn('Using default due date due to error fetching due dates', LogCategory.DATABASE, {
          error: dueDateError,
          action
        });
        return createDefaultDueDate();
      }

      if (!dueDates || dueDates.length === 0) {
        logger.info('No due dates found for action', LogCategory.DATABASE, { action });
        logger.warn('Using default due date as no due dates found for action', LogCategory.DATABASE, { action });
        return createDefaultDueDate();
      }
      
      // First, try to find a record with document_type matching the reportType
      let selectedDueDate = dueDates.find(dd => dd.document_type === reportType);
      
      // If no matching record found, look for one with null document_type
      if (!selectedDueDate) {
        selectedDueDate = dueDates.find(dd => dd.document_type === null);
      }
      
      // If still no record found, use the first one
      if (!selectedDueDate) {
        logger.info('No due date found with matching document type or null document type', LogCategory.GENERAL);
        selectedDueDate = dueDates[0];
      }
            
      // Check if an absolute timestamp is set
      if (selectedDueDate.absolute) {
        return selectedDueDate.absolute;
      }
      
      // If no absolute timestamp, use relative_hours
      const currentDate = new Date();
      const relativeHours = selectedDueDate.relative_hours;
      
      if (relativeHours === null) {
        logger.info('No relative hours found for selected due date', LogCategory.GENERAL, {
          action,
          document_type: selectedDueDate.document_type
        });
        logger.warn('Using default due date as no relative hours found', LogCategory.GENERAL, {
          action,
          document_type: selectedDueDate.document_type
        });
        return createDefaultDueDate();
      }

      // Add the relative hours to the current date
      
      let dueDateTime = new Date(currentDate.getTime() + (relativeHours * 60 * 60 * 1000));
      
      // Adjust for workdays if needed
      dueDateTime = adjustForWorkdays(dueDateTime, selectedDueDate.consider_workdays);
      
      const dueDateTimeString = dueDateTime.toISOString();
      
      return dueDateTimeString;
    } catch (err) {
      logger.error('Error in getDueDate', LogCategory.DATABASE, { error: err });
      error.value = err instanceof Error ? err : new Error(String(err));
      logger.warn('Using default due date due to error in getDueDate', LogCategory.DATABASE, { error: err });
      return createDefaultDueDate();
    } finally {
      loading.value = false;
    }
  }

  return {
    dueDates,
    loading,
    error,
    getDueDate
  };
}